<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'
import Pts2ControllerNav from '../components/pts2/Pts2ControllerNav.vue'
import Pts2DiagnosticsStrip from '../components/pts2/Pts2DiagnosticsStrip.vue'
import { getConfigurationObservation } from '../pts2/configurationHistory.js'
import { configurationFuelGrades } from '../pts2/controllerTabPresentation.js'
import { session } from '../session.js'
import { getControllerSettings, getControllerSummary } from '../pts2/controllers.js'
import {
  confirmPriceOperation,
  createPricePreview,
  getPriceOperation,
  getPriceSnapshot,
  isOperationPollable,
  listPriceOperations,
  normalizePriceInput,
  reconcilePriceOperation,
} from '../pts2/management.js'
import '../pts2/controllers.css'

const route = useRoute()
const router = useRouter()
const controllerId = computed(() => Number(route.params.controllerId))
const scope = computed(() => `${session.user?.id ?? ''}:${session.user?.accessVersion ?? ''}:${controllerId.value}`)
const controller = ref(null)
const settings = ref(null)
const snapshot = ref(null)
const storedObservation = ref(null)
const storedError = ref('')
const usingStoredPrices = computed(() => !snapshot.value?.fuelGrades?.length && !!storedObservation.value)
const displayedGrades = computed(() => usingStoredPrices.value
  ? configurationFuelGrades(storedObservation.value.configuration) : snapshot.value?.fuelGrades ?? [])
const operation = ref(null)
const preview = ref(null)
const loading = ref(true)
const refreshing = ref(false)
const previewing = ref(false)
const confirming = ref(false)
const reconciling = ref(false)
const error = ref('')
const search = ref('')
const rowFilter = ref('ALL')
const draft = reactive({})
const now = ref(Date.now())
const inspectorId = ref(null)
const dialog = ref(null)
const inspector = ref(null)
const priceCard = ref(null)
const draftBar = ref(null)
const tableEndVisible = ref(false)
let request
let operationRequest
let pollTimer
let clockTimer
let dockResizeObserver
let sequence = 0
let disposed = false
let returnFocus = null

const stationId = computed(() => controller.value?.stationId ?? null)
const stationName = computed(() => controller.value?.stationDisplayName ?? `АЗС ${stationId.value ?? '—'}`)
const configuredTransport = computed(() => settings.value?.connectionScheme ?? 'WSS')
const fullCommunication = computed(() => settings.value?.communicationMode !== 'UPLOAD_ONLY')
const observedTransport = computed(() => snapshot.value?.observedTransport ?? settings.value?.observedTransport ?? null)
const transportMismatch = computed(() => observedTransport.value !== null && observedTransport.value !== configuredTransport.value)
const pts2Availability = computed(() => session.applications.find((application) => application.id === 'PTS2')?.availability ?? 'NOT_READY')
const previewOnly = computed(() => pts2Availability.value !== 'READY')
const lockedByOperation = computed(() => operation.value && (isOperationPollable(operation.value)
  || operation.value.state === 'UNKNOWN' && !operation.value.resolved))
const canEdit = computed(() => Boolean(!previewOnly.value && !usingStoredPrices.value && snapshot.value?.fresh && snapshot.value?.complete
  && snapshot.value?.capabilities.priceChange && fullCommunication.value
  && !transportMismatch.value && !lockedByOperation.value))

function draftRow(row) {
  const raw = typeof draft[row.fuelGradeId] === 'string' ? draft[row.fuelGradeId].trim() : ''
  const normalized = raw ? normalizePriceInput(raw) : null
  return {
    ...row,
    raw,
    normalized,
    invalid: Boolean(raw) && normalized === null,
    changed: normalized !== null && normalized !== row.confirmedPrice,
    difference: normalized === null ? null : Number(normalized) - Number(row.confirmedPrice),
  }
}

const rows = computed(() => displayedGrades.value.map(draftRow))
const changedRows = computed(() => rows.value.filter((row) => row.changed))
const hasInvalid = computed(() => rows.value.some((row) => row.invalid))
const problemIds = computed(() => new Set((operation.value?.targetResults ?? [])
  .filter((item) => item.state !== 'APPLIED').map((item) => item.fuelGradeId)))
const visibleRows = computed(() => {
  const query = search.value.trim().toLocaleLowerCase('uk')
  return rows.value.filter((row) => {
    const matchesSearch = !query || `id ${row.fuelGradeId} ${row.name ?? ''}`.toLocaleLowerCase('uk').includes(query)
    const matchesFilter = rowFilter.value === 'ALL' || rowFilter.value === 'CHANGED' && row.changed
      || rowFilter.value === 'PROBLEMS' && problemIds.value.has(row.fuelGradeId)
    return matchesSearch && matchesFilter
  })
})
const previewSeconds = computed(() => preview.value
  ? Math.max(0, Math.ceil((Date.parse(preview.value.expiresAt) - now.value) / 1000)) : 0)
const previewExpired = computed(() => preview.value && (!preview.value.canConfirm || previewSeconds.value <= 0))
const selectedResult = computed(() => operation.value?.targetResults.find((item) => item.fuelGradeId === inspectorId.value) ?? null)
const selectedIntent = computed(() => operation.value?.intent.fuelGrades.find((item) => item.fuelGradeId === inspectorId.value) ?? null)
const operationErrorCode = computed(() => operation.value?.steps.find((step) => step.errorCode)?.errorCode ?? null)

function formatPrice(value) {
  return value == null ? '—' : `${String(value).replace('.', ',')} ₴/л`
}

function formatDifference(value) {
  if (!Number.isFinite(value) || value === 0) return '—'
  return `${value > 0 ? '+' : '−'}${Math.abs(value).toFixed(2).replace('.', ',')} ₴/л`
}

function formatDate(value) {
  const date = value ? new Date(value) : null
  return date && Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat('uk-UA', { dateStyle: 'short', timeStyle: 'short' }).format(date)
    : '—'
}

function operationTitle(value) {
  if (!value) return ''
  if (value.state === 'VERIFIED') return 'Останню зміну підтверджено'
  if (value.state === 'PARTIAL' || value.state === 'UNKNOWN') return 'Результат підтверджено частково'
  if (value.state === 'FAILED' || value.state === 'REJECTED') return 'Зміну не застосовано'
  if (value.state === 'CONFLICT' || value.state === 'CANCELLED') return 'Зміну скасовано до відправлення'
  return 'Перевіряємо результат зміни'
}

function resultView(state) {
  return ({
    APPLIED: { label: 'Застосовано', tone: 'applied' },
    MISMATCH: { label: 'Не збігається', tone: 'problem' },
    ERROR: { label: 'Помилка', tone: 'problem' },
    UNKNOWN: { label: 'Невідомо', tone: 'unknown' },
  })[state] ?? { label: 'Невідомо', tone: 'unknown' }
}

function managementErrorMessage(failure, fallback = 'Не вдалося виконати запит. Оновіть дані та спробуйте ще раз.') {
  const code = failure?.code ?? ''
  if (code.includes('expired')) return 'Попередній перегляд прострочено. Оновіть ціни та створіть новий.'
  if (code.includes('baseline') || code.includes('writer_busy') || code.includes('conflict')) {
    return 'Стан цін змінився. Дані оновлено; перевірте чернетку перед новою спробою.'
  }
  if (code.includes('not_connected') || code.includes('transport') || failure?.status === 503) {
    return 'Контролер втратив придатне підключення до відправлення. Команду не надіслано; дані оновлено.'
  }
  return fallback
}

function clearDraft() {
  for (const key of Object.keys(draft)) delete draft[key]
}

function stopPolling() {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = null
}

function resetScopedState() {
  request?.abort()
  operationRequest?.abort()
  stopPolling()
  closePreview(false)
  closeInspector(false)
  controller.value = null
  settings.value = null
  snapshot.value = null
  storedObservation.value = null
  storedError.value = ''
  operation.value = null
  search.value = ''
  rowFilter.value = 'ALL'
  clearDraft()
}

async function fetchOperation(operationId, signal) {
  if (!operationId) return null
  return getPriceOperation(stationId.value, controllerId.value, operationId, { signal })
}

async function load() {
  resetScopedState()
  request = new AbortController()
  const activeRequest = request
  const generation = ++sequence
  const expectedScope = scope.value
  loading.value = true
  error.value = ''
  try {
    const summary = await getControllerSummary(controllerId.value, { signal: activeRequest.signal })
    if (disposed || generation !== sequence || expectedScope !== scope.value) return
    controller.value = summary
    const [controllerSettings, prices, ledger] = await Promise.all([
      getControllerSettings(summary.stationId, summary.id, { signal: activeRequest.signal }),
      getPriceSnapshot(summary.stationId, summary.id, { signal: activeRequest.signal }),
      listPriceOperations(summary.stationId, summary.id, { limit: 10, signal: activeRequest.signal }),
    ])
    if (disposed || generation !== sequence || expectedScope !== scope.value) return
    settings.value = controllerSettings
    snapshot.value = prices
    await loadStoredPrices(summary, activeRequest.signal)
    const routeOperationId = typeof route.params.operationId === 'string' ? route.params.operationId : null
    const operationId = routeOperationId ?? ledger.items[0]?.id ?? null
    operation.value = operationId ? await fetchOperation(operationId, activeRequest.signal) : null
    if (disposed || generation !== sequence || expectedScope !== scope.value) return
    if (isOperationPollable(operation.value)) startPolling()
  } catch (failure) {
    if (failure?.name !== 'AbortError' && generation === sequence) error.value = managementErrorMessage(failure, 'Сторінка цін недоступна. Перевірте доступ і повторіть спробу.')
  } finally {
    if (!disposed && generation === sequence) loading.value = false
  }
}

async function loadStoredPrices(summary, signal) {
  storedObservation.value = null
  storedError.value = ''
  if (snapshot.value?.fuelGrades?.length) return
  if (!summary.configurationObservationId) {
    storedError.value = 'Збереженої конфігурації цін ще немає.'
    return
  }
  try {
    const observed = await getConfigurationObservation(summary.stationId, summary.id, summary.configurationObservationId, { signal })
    if (!signal.aborted && !disposed) storedObservation.value = observed
  } catch (failure) {
    if (!signal.aborted && !disposed) storedError.value = 'Не вдалося завантажити збережені марки пального. Перевірте доступ і натисніть «Оновити».'
  }
}

async function refreshPrices({ preserveDraft = false } = {}) {
  if (!stationId.value || refreshing.value) return
  operationRequest?.abort()
  operationRequest = new AbortController()
  const active = operationRequest
  refreshing.value = true
  try {
    const prices = await getPriceSnapshot(stationId.value, controllerId.value, { signal: active.signal })
    if (active.signal.aborted || disposed) return
    snapshot.value = prices
    const summary = await getControllerSummary(controllerId.value, { signal: active.signal })
    if (active.signal.aborted || disposed) return
    controller.value = summary
    await loadStoredPrices(summary, active.signal)
    if (!preserveDraft) clearDraft()
  } catch (failure) {
    if (failure?.name !== 'AbortError') error.value = managementErrorMessage(failure, 'Не вдалося оновити підтверджені ціни.')
  } finally {
    if (operationRequest === active) operationRequest = null
    if (!active.signal.aborted) refreshing.value = false
  }
}

async function pollOperation() {
  if (document.hidden || !operation.value || !isOperationPollable(operation.value) || operationRequest) return
  operationRequest = new AbortController()
  const active = operationRequest
  try {
    const value = await getPriceOperation(stationId.value, controllerId.value, operation.value.id, { signal: active.signal })
    if (disposed || active.signal.aborted) return
    operation.value = value
    if (!isOperationPollable(value)) {
      stopPolling()
      await refreshPrices()
    }
  } catch (failure) {
    if (failure?.name !== 'AbortError') error.value = 'Не вдалося оновити стан операції. Повторне надсилання не виконується.'
  } finally {
    if (operationRequest === active) operationRequest = null
  }
}

function startPolling() {
  stopPolling()
  if (isOperationPollable(operation.value)) pollTimer = setInterval(pollOperation, 1500)
}

function resetRow(id) {
  delete draft[id]
}

async function makePreview(event) {
  if (!canEdit.value || changedRows.value.length < 1 || hasInvalid.value || previewing.value) return
  returnFocus = event?.currentTarget ?? document.activeElement
  previewing.value = true
  error.value = ''
  try {
    preview.value = await createPricePreview(stationId.value, controllerId.value, snapshot.value.version,
      changedRows.value.map((row) => ({ fuelGradeId: row.fuelGradeId, price: row.normalized })))
    now.value = Date.now()
    await nextTick()
    dialog.value?.querySelector('button')?.focus()
  } catch (failure) {
    error.value = managementErrorMessage(failure, 'Не вдалося створити попередній перегляд. Дані не надсилалися контролеру.')
    await refreshPrices({ preserveDraft: true })
  } finally {
    previewing.value = false
  }
}

function closePreview(restore = true) {
  preview.value = null
  confirming.value = false
  if (restore) nextTick(() => returnFocus?.focus?.({ preventScroll: true }))
}

async function confirmPreview() {
  if (previewOnly.value || !preview.value || previewExpired.value || confirming.value) return
  confirming.value = true
  error.value = ''
  const currentPreview = preview.value
  try {
    const key = globalThis.crypto.randomUUID()
    operation.value = await confirmPriceOperation(stationId.value, controllerId.value, currentPreview.previewId, key)
    closePreview(false)
    clearDraft()
    await router.replace({ name: 'pts2-price-operation', params: { controllerId: controllerId.value, operationId: operation.value.id } })
    if (isOperationPollable(operation.value)) startPolling()
    else await refreshPrices()
  } catch (failure) {
    const message = managementErrorMessage(failure)
    closePreview(false)
    await refreshPrices()
    error.value = message
  } finally {
    confirming.value = false
  }
}

function openInspector(result, event) {
  returnFocus = event?.currentTarget ?? document.activeElement
  inspectorId.value = result.fuelGradeId
  nextTick(() => inspector.value?.querySelector('button')?.focus())
}

function closeInspector(restore = true) {
  inspectorId.value = null
  if (restore) nextTick(() => returnFocus?.focus?.({ preventScroll: true }))
}

async function reconcile() {
  if (previewOnly.value || !operation.value || reconciling.value) return
  reconciling.value = true
  error.value = ''
  try {
    operation.value = await reconcilePriceOperation(stationId.value, controllerId.value, operation.value.id)
    if (isOperationPollable(operation.value)) startPolling()
    else await refreshPrices()
  } catch (failure) {
    error.value = managementErrorMessage(failure, 'Не вдалося запланувати контрольне читання. Команда зміни ціни не повторювалася.')
  } finally {
    reconciling.value = false
  }
}

function handleEscape(event) {
  if (event.key !== 'Escape') return
  if (preview.value) closePreview()
  else if (inspectorId.value != null) closeInspector()
}

function updateDraftDock() {
  if (!priceCard.value) {
    tableEndVisible.value = false
    return
  }
  const clearance = (draftBar.value?.offsetHeight ?? 96) + 18
  tableEndVisible.value = priceCard.value.getBoundingClientRect().bottom <= window.innerHeight + clearance
}

function observeDraftDock() {
  dockResizeObserver?.disconnect()
  if (priceCard.value) dockResizeObserver?.observe(priceCard.value)
  if (draftBar.value) dockResizeObserver?.observe(draftBar.value)
  updateDraftDock()
}

watch(scope, (value, previous) => { if (previous && value !== previous) load() })
watch(() => route.params.operationId, async (value, previous) => {
  if (!loading.value && typeof value === 'string' && value !== previous && value !== operation.value?.id) {
    operationRequest?.abort()
    operationRequest = new AbortController()
    try {
      operation.value = await fetchOperation(value, operationRequest.signal)
      startPolling()
    } catch (failure) {
      if (failure?.name !== 'AbortError') error.value = managementErrorMessage(failure)
    } finally {
      operationRequest = null
    }
  }
})
watch(() => [loading.value, visibleRows.value.length], () => nextTick(observeDraftDock))

onMounted(() => {
  document.documentElement.classList.add('pts2-prices-open')
  clockTimer = setInterval(() => { now.value = Date.now() }, 1000)
  document.addEventListener('keydown', handleEscape)
  window.addEventListener('scroll', updateDraftDock, { passive: true })
  window.addEventListener('resize', updateDraftDock, { passive: true })
  if ('ResizeObserver' in globalThis) dockResizeObserver = new ResizeObserver(updateDraftDock)
  load()
})
onBeforeUnmount(() => {
  disposed = true
  sequence++
  request?.abort()
  operationRequest?.abort()
  stopPolling()
  clearInterval(clockTimer)
  document.removeEventListener('keydown', handleEscape)
  window.removeEventListener('scroll', updateDraftDock)
  window.removeEventListener('resize', updateDraftDock)
  dockResizeObserver?.disconnect()
  document.documentElement.classList.remove('pts2-prices-open')
})
</script>

<template>
  <section class="pc-page pc-detail pts2-prices-page" aria-labelledby="pts2-prices-title">
    <nav class="pc-breadcrumb" aria-label="Навігаційний шлях">
      <RouterLink :to="{ name: 'pts2-controllers' }">Контролери</RouterLink>
      <ChevronRightIcon aria-hidden="true" />
      <RouterLink v-if="controller" :to="{ name: 'pts2-controller-detail', params: { controllerId } }">{{ controller.displayName }}</RouterLink>
      <span v-else>Ціни</span>
      <template v-if="controller"><ChevronRightIcon aria-hidden="true" /><span>Ціни</span></template>
    </nav>

    <div v-if="loading" class="pc-table-loading" role="status">Завантажуємо ціни контролера…</div>
    <template v-else-if="controller && snapshot">
      <header class="pc-heading pc-detail-heading pts2-prices-heading">
        <div>
          <div class="pts2-heading-line">
            <h1 id="pts2-prices-title">{{ controller.displayName }}</h1>
            <span class="pc-state" :class="controller.connectionFreshness === 'FRESH' ? 'fresh' : 'unknown'"><i></i>{{ controller.connectionFreshness === 'FRESH' ? 'Онлайн' : 'Стан невідомий' }}</span>
          </div>
          <p>{{ controller.stableCode }} · {{ stationName }} · Останні дані: {{ formatDate(controller.lastSeenAt) }}</p>
        </div>
        <button class="pc-secondary" type="button" :disabled="refreshing" @click="refreshPrices({ preserveDraft: true })">
          <ArrowPathIcon aria-hidden="true" />{{ refreshing ? 'Оновлюємо…' : 'Оновити' }}
        </button>
      </header>

      <p v-if="error" class="pc-notice pts2-price-error" role="alert">{{ error }}</p>
      <p v-if="previewOnly" class="pc-notice pts2-price-preview" role="status"><LockClosedIcon aria-hidden="true" />PTS-2 працює як preview: підтверджені дані доступні для читання, команди обладнанню заблоковані до release gate.</p>
      <Pts2DiagnosticsStrip :snapshot="controller.diagnostics" :summary="controller" :failed="false" />
      <Pts2ControllerNav :controller-id="controllerId" active="prices" />

      <div v-if="configuredTransport === 'WS' || configuredTransport === 'HTTP'" class="pts2-price-transport ws" role="alert">
        <ExclamationTriangleIcon aria-hidden="true" />
        <div><strong>{{ configuredTransport }} не шифрує трафік</strong><p>{{ configuredTransport === 'WS' ? 'WebSocket' : 'HTTP' }}-трафік передається без TLS. Використовуйте {{ configuredTransport === 'WS' ? 'WSS' : 'HTTPS' }}, коли контролер і мережа це підтримують.</p></div>
      </div>
      <div v-if="!fullCommunication" class="pts2-price-transport ws" role="alert">
        <LockClosedIcon aria-hidden="true" />
        <div><strong>Сумісний режим передачі даних</strong><span>Контролер передає дані через {{ configuredTransport }}, але пряме редагування цін і команди вимкнені.</span></div>
      </div>
      <div v-else-if="configuredTransport === 'WSS'" class="pts2-price-transport secure"><LockClosedIcon aria-hidden="true" />WSS · Захищене з’єднання</div>
      <div v-if="transportMismatch" class="pts2-price-transport mismatch" role="alert">
        <ExclamationTriangleIcon aria-hidden="true" />Налаштовано {{ configuredTransport }}, фактичне підключення {{ observedTransport }}. Зміна цін недоступна.
      </div>

      <section v-if="operation" class="pts2-operation-strip" :class="operation.state.toLowerCase()" aria-labelledby="last-price-operation">
        <ClockIcon aria-hidden="true" />
        <div class="pts2-operation-copy">
          <strong id="last-price-operation">{{ operationTitle(operation) }}</strong>
          <small>{{ formatDate(operation.completedAt ?? operation.createdAt) }} · {{ operation.actorUsername }}</small>
        </div>
        <div class="pts2-result-chips" aria-label="Результати за кодами пального">
          <button
            v-for="result in operation.targetResults"
            :key="result.fuelGradeId"
            type="button"
            :class="resultView(result.state).tone"
            :aria-label="`ID ${result.fuelGradeId}: ${resultView(result.state).label}. Відкрити перевірку`"
            @click="openInspector(result, $event)"
          >
            <CheckCircleIcon v-if="result.state === 'APPLIED'" aria-hidden="true" />
            <ClockIcon v-else aria-hidden="true" />
            ID {{ result.fuelGradeId }} — {{ resultView(result.state).label }}
          </button>
        </div>
        <span v-if="isOperationPollable(operation)" class="pts2-operation-progress" role="status"><ArrowPathIcon aria-hidden="true" />Перевіряємо…</span>
      </section>

      <section ref="priceCard" class="pts2-price-card" aria-labelledby="price-card-title">
        <header class="pts2-price-card-heading">
          <div>
            <h2 id="price-card-title">Ціни пального</h2>
            <p>Ціна встановлюється окремо для кожного коду палива PTS-2.</p>
          </div>
          <div class="pts2-price-tools">
            <label class="pts2-price-search"><MagnifyingGlassIcon aria-hidden="true" /><span class="sr-only">Пошук</span><input v-model="search" type="search" placeholder="Назва або fuelId" /></label>
            <label><span class="sr-only">Фільтр рядків</span><select v-model="rowFilter"><option value="ALL">Усі коди</option><option value="CHANGED">Змінені</option><option value="PROBLEMS">Потребують перевірки</option></select></label>
          </div>
          <p class="pts2-price-verified"><InformationCircleIcon aria-hidden="true" />{{ usingStoredPrices ? 'Збережена конфігурація · ' + formatDate(storedObservation.observedAt) : snapshot?.fresh ? 'Актуальне підтверджене читання' : 'Актуальні ціни не підтверджено' }}</p>
        </header>

        <p v-if="usingStoredPrices" class="pc-notice" role="status">Показано марки та наявні ціни зі збереженої конфігурації. Актуальне читання цін не отримано; ці значення доступні лише для перегляду.</p>
        <p v-else-if="!snapshot?.fresh" class="pc-notice" role="status">Актуальність цін не підтверджена. Зміна цін недоступна.</p>
        <p v-if="storedError" class="pc-notice" role="alert">{{ storedError }}</p>
        <div class="pts2-price-table-wrap">
          <table class="pts2-price-table">
            <thead><tr><th>Код палива</th><th>Назва</th><th>{{ usingStoredPrices ? 'Ціна з конфігурації' : 'Підтверджена ціна' }}</th><th>Нова ціна</th><th>Різниця</th><th>Чернетка</th></tr></thead>
            <tbody>
              <tr v-for="row in visibleRows" :key="row.fuelGradeId" :class="{ changed: row.changed, problem: problemIds.has(row.fuelGradeId) }">
                <td><strong class="pts2-fuel-id">ID {{ row.fuelGradeId }}</strong></td>
                <th scope="row">{{ row.name ?? 'Без назви' }}</th>
                <td>{{ formatPrice(row.confirmedPrice) }}</td>
                <td>
                  <label class="pts2-price-input" :class="{ invalid: row.invalid }">
                    <span class="sr-only">Нова ціна для ID {{ row.fuelGradeId }}</span>
                    <input
                      :value="draft[row.fuelGradeId] ?? ''"
                      inputmode="decimal"
                      autocomplete="off"
                      :disabled="!canEdit"
                      :aria-invalid="row.invalid"
                      placeholder="—"
                      @input="draft[row.fuelGradeId] = $event.target.value"
                    />
                    <span>₴/л</span>
                  </label>
                  <small v-if="row.invalid" class="pts2-input-error">До 9 цифр і 3 знаків після коми</small>
                </td>
                <td :class="{ positive: row.difference > 0, negative: row.difference < 0 }">{{ row.changed ? formatDifference(row.difference) : '—' }}</td>
                <td>
                  <button v-if="row.changed || row.invalid" class="pts2-row-reset" type="button" :aria-label="`Скинути зміну ID ${row.fuelGradeId}`" @click="resetRow(row.fuelGradeId)"><ArrowUturnLeftIcon aria-hidden="true" />Скинути</button>
                  <span v-else-if="problemIds.has(row.fuelGradeId)" class="pts2-row-state unknown"><ClockIcon aria-hidden="true" />Потрібна перевірка</span>
                  <span v-else class="pts2-row-state"><i></i>Без змін</span>
                </td>
              </tr>
              <tr v-if="visibleRows.length === 0"><td colspan="6" class="pts2-price-empty">{{ search || rowFilter !== 'ALL' ? 'За цим фільтром кодів пального немає.' : 'Даних про марки пального ще не отримано.' }}</td></tr>
            </tbody>
          </table>
        </div>
        <footer class="pts2-table-footer">Показано: {{ visibleRows.length }}</footer>
      </section>

      <footer ref="draftBar" class="pts2-draft-bar" :class="{ active: changedRows.length > 0, 'at-table-end': tableEndVisible }">
        <DocumentTextIcon aria-hidden="true" />
        <div><strong>{{ changedRows.length }} {{ changedRows.length === 1 ? 'зміна' : 'зміни' }} у чернетці</strong><small>Попередній перегляд діятиме 60 секунд</small></div>
        <button class="pc-secondary" type="button" :disabled="changedRows.length === 0" @click="clearDraft">Скинути</button>
        <button class="pc-primary" type="button" :disabled="!canEdit || changedRows.length === 0 || hasInvalid || previewing" @click="makePreview">
          {{ previewing ? 'Готуємо…' : `Переглянути ${changedRows.length} ${changedRows.length === 1 ? 'зміну' : 'зміни'}` }}
        </button>
      </footer>
      <p v-if="!canEdit" class="pts2-capability-note" role="status">
        Зміна цін доступна лише для свіжої повної карти та активного підключення з відповідною схемою. Невідома операція спершу потребує контрольного читання.
      </p>
    </template>
    <div v-else class="pc-empty"><ExclamationTriangleIcon aria-hidden="true" /><h2>Ціни контролера недоступні</h2><button type="button" @click="load">Спробувати ще раз</button></div>

    <div v-if="preview" class="pts2-modal-backdrop" @mousedown.self="closePreview()">
      <section ref="dialog" class="pts2-preview-dialog" role="dialog" aria-modal="true" aria-labelledby="price-preview-title">
        <header><div><h2 id="price-preview-title">Перевірте зміни цін</h2><p>{{ controller.displayName }} · {{ stationName }} · {{ configuredTransport }}</p></div><button type="button" aria-label="Закрити попередній перегляд" @click="closePreview()"><XMarkIcon aria-hidden="true" /></button></header>
        <div class="pts2-preview-timer" :class="{ expired: previewExpired }"><ClockIcon aria-hidden="true" /><strong>{{ previewExpired ? 'Час вичерпано' : `${previewSeconds} с до завершення` }}</strong><span>Це application preview; команда ще не надсилалася.</span></div>
        <div class="pts2-preview-list">
          <article v-for="item in preview.intent.fuelGrades" :key="item.fuelGradeId">
            <strong class="pts2-fuel-id">ID {{ item.fuelGradeId }}</strong><span>{{ item.name ?? 'Без назви' }}</span>
            <span>{{ formatPrice(item.before) }} <ChevronRightIcon aria-hidden="true" /> <b>{{ formatPrice(item.after) }}</b></span>
            <em>{{ formatDifference(Number(item.after) - Number(item.before)) }}</em>
          </article>
        </div>
        <p v-if="previewExpired" class="pts2-preview-expired" role="alert">Створіть новий попередній перегляд із актуального baseline.</p>
        <footer><button class="pc-secondary" type="button" @click="closePreview()">Повернутися до чернетки</button><button class="pc-primary" type="button" :disabled="previewOnly || previewExpired || confirming" @click="confirmPreview"><ArrowPathIcon v-if="confirming" class="spin" aria-hidden="true" />{{ confirming ? 'Надсилаємо й перевіряємо…' : 'Підтвердити зміну цін' }}</button></footer>
      </section>
    </div>

    <div v-if="selectedResult" class="pts2-inspector-backdrop" @mousedown.self="closeInspector()">
      <aside ref="inspector" class="pts2-inspector" role="dialog" aria-modal="true" aria-labelledby="price-inspector-title">
        <header><h2 id="price-inspector-title">Перевірка коду палива</h2><button type="button" aria-label="Закрити перевірку" @click="closeInspector()"><XMarkIcon aria-hidden="true" /></button></header>
        <div class="pts2-inspector-state"><strong class="pts2-fuel-id">ID {{ selectedResult.fuelGradeId }}</strong><span :class="resultView(selectedResult.state).tone"><ClockIcon aria-hidden="true" />{{ resultView(selectedResult.state).label }}</span></div>
        <dl>
          <div><dt>Назва палива</dt><dd>{{ selectedIntent?.name ?? 'Без назви' }}</dd></div>
          <div><dt>Очікувана ціна</dt><dd>{{ formatPrice(selectedResult.expectedPrice) }}</dd></div>
          <div><dt>Остання підтверджена</dt><dd>{{ formatPrice(selectedResult.confirmedPrice ?? selectedIntent?.before) }}</dd></div>
          <div><dt>Безпечний код перевірки</dt><dd>{{ operationErrorCode ?? '—' }}</dd></div>
        </dl>
        <div v-if="selectedResult.state !== 'APPLIED'" class="pts2-inspector-warning"><ExclamationTriangleIcon aria-hidden="true" />Контролер не підтвердив очікувану ціну. Перед новою операцією виконайте контрольне читання.</div>
        <button v-if="selectedResult.state !== 'APPLIED'" class="pc-primary" type="button" :disabled="previewOnly || reconciling" @click="reconcile"><ArrowPathIcon aria-hidden="true" />{{ reconciling ? 'Перевіряємо…' : 'Перевірити поточну ціну' }}</button>
        <button class="pc-secondary" type="button" @click="closeInspector()">Закрити</button>
        <details><summary>Відкрити технічні деталі</summary><p>Операція {{ operation.id }}. Контрольне читання не повторює команду зміни ціни.</p></details>
      </aside>
    </div>
  </section>
</template>

<style scoped>
:global(html.pts2-prices-open), :global(html.pts2-prices-open body) { overflow-x: clip; }
.pts2-prices-page { width: 100%; min-width: 0; max-width: 1280px; padding-bottom: 110px; overflow-x: hidden; }
.pts2-prices-heading { align-items: flex-start; margin-bottom: 16px; }
.pts2-heading-line { display: flex; align-items: center; gap: 18px; }
.pts2-heading-line h1 { margin: 0; }
.pts2-prices-heading .pc-secondary svg { width: 18px; height: 18px; }
.pts2-price-error { margin-bottom: 14px; }
.pts2-price-preview { display: flex; align-items: center; gap: 9px; margin-bottom: 14px; color: #715314; background: #fff7dc; }
.pts2-price-preview svg { width: 20px; height: 20px; flex: 0 0 auto; }
.pts2-price-transport { display: flex; width: fit-content; align-items: flex-start; gap: 9px; margin: 0 0 14px; padding: 8px 12px; border-radius: 7px; font-size: 13px; }
.pts2-price-transport svg { width: 19px; height: 19px; flex: 0 0 auto; }
.pts2-price-transport p { margin: 3px 0 0; }
.pts2-price-transport.secure { color: #166844; background: #e6f5ed; font-weight: 700; }
.pts2-price-transport.ws, .pts2-price-transport.mismatch { width: auto; border: 1px solid #edbd65; color: #78500b; background: #fff8e9; }
.pts2-operation-strip { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; padding: 12px 14px; border: 1px solid #e6b65e; border-radius: 7px; background: #fff9ec; }
.pts2-operation-strip > svg { width: 28px; height: 28px; color: #e39a00; }
.pts2-operation-copy { display: grid; min-width: 215px; gap: 4px; }
.pts2-operation-copy small { color: #617681; }
.pts2-result-chips { display: flex; min-width: 0; flex: 1; gap: 8px; overflow-x: auto; }
.pts2-result-chips button { display: inline-flex; min-width: max-content; align-items: center; gap: 5px; padding: 7px 10px; border: 0; border-radius: 7px; font-size: 12px; cursor: pointer; }
.pts2-result-chips svg { width: 16px; height: 16px; }
.pts2-result-chips .applied { color: #06714d; background: #e2f4ec; }
.pts2-result-chips .problem, .pts2-result-chips .unknown { color: #985b00; background: #fff0d5; }
.pts2-operation-progress { display: inline-flex; align-items: center; gap: 7px; color: #176b7c; font-weight: 700; }
.pts2-operation-progress svg { width: 18px; height: 18px; animation: spin 1.2s linear infinite; }
.pts2-price-card { min-width: 0; overflow: hidden; border: 1px solid #cedadd; border-radius: 8px; background: #fff; box-shadow: 0 2px 8px #183e4509; }
.pts2-price-card-heading { position: relative; display: flex; min-height: 102px; align-items: flex-end; justify-content: space-between; gap: 18px; padding: 18px 16px 15px; border-bottom: 1px solid #d7e0e3; }
.pts2-price-card-heading h2 { margin: 0; color: #09294b; font-size: 24px; }
.pts2-price-card-heading p { margin: 5px 0 0; color: #677b86; }
.pts2-price-verified { position: absolute; top: 14px; right: 16px; display: flex; align-items: center; gap: 7px; font-size: 12px; }
.pts2-price-verified svg { width: 18px; height: 18px; }
.pts2-price-tools { display: flex; gap: 10px; }
.pts2-price-search { display: flex; min-width: 230px; align-items: center; gap: 7px; padding: 0 11px; border: 1px solid #cdd8dd; border-radius: 5px; }
.pts2-price-search svg { width: 20px; height: 20px; color: #637985; }
.pts2-price-search input, .pts2-price-tools select { min-height: 39px; border: 0; outline: 0; color: #17384d; background: transparent; }
.pts2-price-tools select { min-width: 130px; padding: 0 9px; border: 1px solid #cdd8dd; border-radius: 5px; background: #fff; }
.pts2-price-table-wrap { width: 100%; max-width: 100%; overflow-x: auto; }
.pts2-price-table { width: 100%; min-width: 820px; border-collapse: collapse; }
.pts2-price-table th, .pts2-price-table td { height: 54px; padding: 9px 14px; border-bottom: 1px solid #dce4e7; color: #183c58; text-align: left; white-space: nowrap; }
.pts2-price-table thead th { height: 43px; color: #557080; background: #f6f8f9; font-size: 12px; font-weight: 600; }
.pts2-price-table tbody th { font-weight: 650; }
.pts2-price-table tr.changed { background: #eef9f8; }
.pts2-price-table tr.problem { background: #fff7e8; }
.pts2-fuel-id { display: inline-flex; padding: 6px 10px; border-radius: 5px; color: #153b64; background: #edf1f6; font-family: ui-monospace, SFMono-Regular, Consolas, monospace; font-size: 13px; }
.pts2-price-input { position: relative; display: inline-flex; align-items: center; border: 1px solid #cdd8de; border-radius: 5px; background: #fff; }
.pts2-price-input:focus-within { border-color: #008096; box-shadow: 0 0 0 2px #8bd0da66; }
.pts2-price-input.invalid { border-color: #b84545; }
.pts2-price-input input { width: 88px; min-height: 34px; padding: 5px 9px; border: 0; outline: 0; color: #153b59; background: transparent; font: inherit; }
.pts2-price-input > span { padding: 5px 8px; border-left: 1px solid #d8e1e4; color: #607781; background: #f5f7f8; }
.pts2-input-error { display: block; margin-top: 4px; color: #a33838; }
.pts2-price-table .positive { color: #05825b; font-weight: 700; }
.pts2-price-table .negative { color: #a44949; font-weight: 700; }
.pts2-row-reset { display: inline-flex; align-items: center; gap: 5px; padding: 5px; border: 0; color: #00768a; background: transparent; cursor: pointer; }
.pts2-row-reset svg, .pts2-row-state svg { width: 17px; height: 17px; }
.pts2-row-state { display: inline-flex; align-items: center; gap: 7px; color: #627887; }
.pts2-row-state i { width: 10px; height: 10px; border-radius: 50%; background: #7c8fa2; }
.pts2-row-state.unknown { color: #a66500; }
.pts2-price-empty { color: #6d7e87 !important; text-align: center !important; }
.pts2-table-footer { padding: 11px 14px; color: #657a85; font-size: 12px; }
.pts2-draft-bar { position: sticky; z-index: 5; bottom: 0; display: grid; grid-template-columns: auto minmax(160px, 1fr) auto auto; align-items: center; gap: 16px; margin: 18px 0 -84px; padding: 14px 16px; border: 1px solid #cbd9dd; border-radius: 7px; background: #fff; box-shadow: 0 -5px 18px #193d4820; }
.pts2-draft-bar.at-table-end { position: static; margin-bottom: 0; }
.pts2-draft-bar > svg { width: 31px; height: 31px; color: #007b91; }
.pts2-draft-bar > div { display: grid; gap: 3px; }
.pts2-draft-bar small { color: #607580; }
.pts2-draft-bar:not(.active) { opacity: .78; }
.pts2-capability-note { margin: 94px 0 -80px; color: #6b7d87; font-size: 13px; }
.pts2-modal-backdrop, .pts2-inspector-backdrop { position: fixed; z-index: 80; inset: 0; display: grid; place-items: center; padding: 20px; background: #072a3766; }
.pts2-preview-dialog { width: min(720px, 100%); max-height: calc(100vh - 40px); overflow-y: auto; border-radius: 9px; background: #fff; box-shadow: 0 20px 60px #09263255; }
.pts2-preview-dialog > header, .pts2-inspector > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 20px; border-bottom: 1px solid #d6e0e3; }
.pts2-preview-dialog h2, .pts2-inspector h2 { margin: 0; color: #0b2949; font-size: 22px; }
.pts2-preview-dialog header p { margin: 5px 0 0; color: #647984; }
.pts2-preview-dialog header button, .pts2-inspector header button { padding: 3px; border: 0; color: #617782; background: transparent; cursor: pointer; }
.pts2-preview-dialog header svg, .pts2-inspector header svg { width: 24px; height: 24px; }
.pts2-preview-timer { display: grid; grid-template-columns: auto auto 1fr; align-items: center; gap: 9px; margin: 18px 20px; padding: 11px 13px; border-radius: 7px; color: #176a48; background: #eaf7f1; }
.pts2-preview-timer.expired { color: #985b00; background: #fff2dc; }
.pts2-preview-timer svg { width: 21px; height: 21px; }
.pts2-preview-timer span { color: #637985; font-size: 12px; text-align: right; }
.pts2-preview-list { display: grid; gap: 1px; margin: 0 20px; border: 1px solid #d5dfe2; border-radius: 7px; overflow: hidden; background: #d5dfe2; }
.pts2-preview-list article { display: grid; grid-template-columns: auto minmax(100px, 1fr) minmax(210px, auto) auto; align-items: center; gap: 13px; padding: 13px; background: #fff; }
.pts2-preview-list article > span:last-of-type { display: inline-flex; align-items: center; gap: 6px; }
.pts2-preview-list article svg { width: 15px; height: 15px; }
.pts2-preview-list em { color: #087c5c; font-style: normal; font-weight: 700; }
.pts2-preview-expired { margin: 14px 20px 0; color: #9b5b00; }
.pts2-preview-dialog > footer { display: flex; justify-content: flex-end; gap: 10px; padding: 18px 20px 20px; }
.spin { animation: spin 1s linear infinite; }
.pts2-inspector-backdrop { place-items: stretch end; padding: 0; background: transparent; }
.pts2-inspector { width: min(380px, 100%); height: 100%; overflow-y: auto; padding-bottom: 24px; background: #fff; box-shadow: -15px 0 40px #09263233; }
.pts2-inspector-state { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 18px 20px; }
.pts2-inspector-state > span { display: inline-flex; align-items: center; gap: 6px; padding: 8px 11px; border-radius: 8px; color: #965c00; background: #fff1d8; }
.pts2-inspector-state svg { width: 18px; height: 18px; }
.pts2-inspector dl { display: grid; gap: 15px; margin: 0; padding: 0 20px 18px; }
.pts2-inspector dl div { display: grid; gap: 6px; }
.pts2-inspector dt { color: #607683; font-size: 13px; }
.pts2-inspector dd { margin: 0; padding: 11px 12px; border: 1px solid #d3dde1; border-radius: 5px; color: #173a57; background: #f8fafb; }
.pts2-inspector-warning { display: flex; gap: 9px; margin: 0 20px 18px; padding: 13px; border: 1px solid #ebb758; border-radius: 6px; color: #704808; background: #fff8e9; line-height: 1.45; }
.pts2-inspector-warning svg { width: 23px; height: 23px; flex: 0 0 auto; }
.pts2-inspector > .pc-primary, .pts2-inspector > .pc-secondary { width: calc(100% - 40px); margin: 0 20px 12px; }
.pts2-inspector details { margin: 12px 20px; color: #176a7c; }
.pts2-inspector details p { color: #607580; overflow-wrap: anywhere; }
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 760px) {
  .pts2-prices-page { padding-bottom: 150px; }
  .pts2-prices-heading { flex-direction: column; }
  .pts2-prices-heading .pc-secondary { width: 100%; }
  .pts2-heading-line { flex-wrap: wrap; }
  .pts2-operation-strip { align-items: flex-start; flex-wrap: wrap; }
  .pts2-operation-copy { min-width: 0; flex: 1; }
  .pts2-result-chips { flex-basis: 100%; }
  .pts2-price-card-heading { align-items: stretch; flex-direction: column; padding-top: 44px; }
  .pts2-price-tools { flex-direction: column; }
  .pts2-price-search { min-width: 0; }
  .pts2-price-tools select { width: 100%; }
  .pts2-price-table th:first-child, .pts2-price-table td:first-child { position: sticky; z-index: 2; left: 0; background: inherit; }
  .pts2-price-table th:nth-child(2), .pts2-price-table tbody th { position: sticky; z-index: 2; left: 88px; background: inherit; box-shadow: 6px 0 8px #1d46500d; }
  .pts2-price-table thead th:first-child, .pts2-price-table thead th:nth-child(2) { z-index: 3; background: #f6f8f9; }
  .pts2-draft-bar { grid-template-columns: auto 1fr; margin-bottom: -132px; }
  .pts2-draft-bar.at-table-end { margin-bottom: 0; }
  .pts2-draft-bar .pc-primary, .pts2-draft-bar .pc-secondary { width: 100%; }
  .pts2-preview-list article { grid-template-columns: auto 1fr; }
  .pts2-preview-list article > span:last-of-type, .pts2-preview-list article em { grid-column: 2; }
  .pts2-preview-dialog > footer { flex-direction: column-reverse; }
  .pts2-preview-dialog > footer button { width: 100%; }
  .pts2-inspector-backdrop { align-items: end; background: #072a3766; }
  .pts2-inspector { width: 100%; height: min(78vh, 700px); border-radius: 12px 12px 0 0; }
}
</style>
